import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { HoldItemProvider } from '@euler/components/holdmenu/HoldMenu';
import { LoadingProvider } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsTabletLayout } from '@euler/functions/responsiveness';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { NavigationAction, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { AnimatePresence, MotiView } from 'moti';
import {
  ReactNode,
  Suspense,
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  KeyboardAvoidingView,
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { Easing } from 'react-native-reanimated';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { Slot } from 'react-slot-fill';
import { CSSProp } from 'styled-components';

export type AsideProps = {
  width?: number;
  dismissible?: boolean;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  backdropCss?: CSSProp;
  fallback?: NonNullable<ReactNode> | null;
  disableDefaultNavigationHandler?: boolean;
  withoutKeyboardAvoidingView?: boolean;
  onDismiss?: () => void;
  onDismissed?: () => boolean;
};

export type AsideRef = {
  dismiss: (action?: NavigationAction) => void;
};

export const asideNavigationOptions = () => {};

const kMultiTaskIndicatorWidth = 75;

export const useAsideWidth = (preferredWidth?: number) => {
  const { rpx, dimens } = useResponsiveContext();
  const isTablet = useIsTabletLayout();
  let defaultWidth = Math.max(rpx(500), dimens.width * 0.45);
  if (isTablet) {
    const halfWidth = dimens.width / 2;
    if (Math.abs(halfWidth - defaultWidth) < kMultiTaskIndicatorWidth) {
      if (defaultWidth > halfWidth) {
        defaultWidth = halfWidth + kMultiTaskIndicatorWidth;
      } else {
        defaultWidth = halfWidth - kMultiTaskIndicatorWidth;
      }
    }
  }
  return preferredWidth ?? defaultWidth;
};

type AsideContextProps = {
  width: number;
  dismiss: (action?: NavigationAction) => void;
};

export const AsideContext = createContext<AsideContextProps>(null as any);

export const useAsideContext = () => useContext(AsideContext);

export const useIsAsideLayout = () => {
  return useContext(AsideContext) != null;
};

export const Aside = memo(
  forwardRef<AsideRef, AsideProps>((props, ref) => {
    const {
      disableDefaultNavigationHandler,
      withoutKeyboardAvoidingView,
      dismissible,
      onDismiss,
      onDismissed,
    } = props;
    const { rpx } = useResponsiveContext();
    const { top, bottom } = useSafeAreaInsets();
    const { navBarHeight } = useSystemMetrics();
    const width = useAsideWidth(props.width);
    const [show, setShow] = useState(true);

    const navigation = useNavigation<StackNavigationProp<any>>();
    const continueAction = useRef<NavigationAction>();

    const dismiss = useCallback(
      (action?: NavigationAction) => {
        if (action != null) {
          continueAction.current = action;
        }
        onDismiss?.();
        setShow(false);
      },
      [onDismiss],
    );

    useImperativeHandle(ref, () => ({ dismiss }));

    const handleDismiss = useCallback(() => {
      if (dismissible !== false) {
        dismiss();
      }
    }, [dismiss, dismissible]);

    useEffect(() => {
      if (!show || disableDefaultNavigationHandler) {
        return;
      }
      return navigation.addListener('beforeRemove', e => {
        e.preventDefault();
        continueAction.current = e.data.action;
        setShow(false);
      });
    }, [disableDefaultNavigationHandler, navigation, show]);

    const onExitComplete = useCallback(() => {
      const res = onDismissed?.();
      if (res === true) {
        return;
      }
      if (continueAction.current != null) {
        navigation.dispatch(continueAction.current);
      } else {
        navigation.goBack();
      }
    }, [navigation, onDismissed]);

    const contextValue = useMemo(() => ({ width, dismiss }), [width, dismiss]);

    return (
      <AsideContext.Provider value={contextValue}>
        <AnimatePresence onExitComplete={onExitComplete}>
          {show && (
            <>
              <MotiView
                from={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'timing', duration: 300 }}
                style={StyleSheet.absoluteFill}
                exitTransition={{
                  type: 'spring',
                  overshootClamping: true,
                }}
                css={`
                  padding-top: ${top}px;
                  background-color: rgba(0, 0, 0, 0.6);
                  ${props.backdropCss};
                `}
              >
                <TouchableWithoutFeedback onPress={handleDismiss}>
                  <View style={StyleSheet.absoluteFill} />
                </TouchableWithoutFeedback>
              </MotiView>

              <KeyboardAvoidingView
                enabled={!withoutKeyboardAvoidingView}
                behavior={'padding'}
                css={`
                  flex: 1;
                  width: ${width}px;
                  position: absolute;
                  right: 0;
                  top: 0;
                  bottom: 0;
                `}
              >
                <MotiView
                  css={`
                    background-color: #fff;
                    overflow: hidden;
                    align-self: center;
                    flex: 1;
                    width: ${width}px;
                    height: 100%;
                  `}
                  style={[StyleSheet.absoluteFill, props.style]}
                  from={{ translateX: width }}
                  animate={{ translateX: 0 }}
                  transition={{
                    type: 'timing',
                    duration: 250,
                    easing: Easing.in(Easing.linear),
                  }}
                  exit={{ translateX: width }}
                  exitTransition={{
                    type: 'spring',
                    overshootClamping: true,
                  }}
                >
                  <LoadingProvider name="aside">
                    <Suspense
                      fallback={
                        props.fallback === undefined ? (
                          <ScrollView
                            css={`
                              flex: 1;
                            `}
                            contentContainerStyle={[
                              {
                                flexGrow: 1,
                                paddingTop: navBarHeight + rpx(15),
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                              },
                            ]}
                          >
                            <DefaultLoader />
                          </ScrollView>
                        ) : (
                          props.fallback
                        )
                      }
                    >
                      <SafeAreaInsetsContext.Provider
                        value={{
                          top: 0,
                          right: 0,
                          bottom,
                          left: 0,
                        }}
                      >
                        <ActionSheetProvider>
                          <HoldItemProvider>{props.children}</HoldItemProvider>
                        </ActionSheetProvider>
                      </SafeAreaInsetsContext.Provider>
                    </Suspense>
                  </LoadingProvider>
                  <Slot name="aside" />
                </MotiView>
              </KeyboardAvoidingView>
            </>
          )}
        </AnimatePresence>
      </AsideContext.Provider>
    );
  }),
);
